import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Share2, Trash2, CheckSquare, DollarSign, FolderOpen, Loader2, Package, Car, Store, Repeat, Layers, Fuel, Wrench, Shield, FileCheck } from 'lucide-react';
import { useEscapeToHome } from '@/hooks/useKeyboard';

const TYPE_ICONS = {
  task: CheckSquare, expense: DollarSign, project: FolderOpen, habit: Repeat,
  asset: Package, vehicle: Car, vendor: Store, task_sub: Layers,
  asset_sub: Layers, vehicle_sub: Layers,
};
const SUB_COLL_ICONS = {
  fuel_logs: Fuel, service_logs: Wrench, vehicle_expenses: DollarSign,
  insurance_records: Shield, puc_records: FileCheck, compliance_records: Shield,
  subtasks: CheckSquare,
};
const TYPE_LABELS = {
  task: 'task', expense: 'expense', project: 'workspace', habit: 'habit',
  asset: 'asset', vehicle: 'vehicle', vendor: 'vendor',
  task_sub: 'subtask', asset_sub: 'asset record', vehicle_sub: 'vehicle record',
};

function recordTitle(r) {
  const rec = r.record || {};
  if (['asset_sub', 'vehicle_sub', 'task_sub'].includes(r.record_type)) {
    const sub = rec.sub || {};
    const collLabel = (r.sub_collection || '').replace(/_/g, ' ');
    const subName = sub.title || sub.service_type || sub.expense_type || sub.compliance_type || sub.company || sub.fuel_grade || sub.vendor_name || sub.description || collLabel;
    return { primary: `${subName}`, secondary: `${rec.parent_type === 'vehicle' ? 'Vehicle' : rec.parent_type === 'task' ? 'Task' : 'Asset'}: ${rec.parent_name} · ${collLabel}` };
  }
  return { primary: rec.title || rec.name || '—', secondary: null };
}

export default function SharedWithMePage() {
  const [tab, setTab] = useState('received');
  useEscapeToHome();
  const [receivedRecords, setReceivedRecords] = useState([]);
  const [sentRecords, setSentRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReceived = useCallback(async () => {
    try { const { data } = await api.get('/shared-with-me'); setReceivedRecords(data.shared_records); } catch {}
  }, []);

  const fetchSent = useCallback(async () => {
    try { const { data } = await api.get('/shared-by-me'); setSentRecords(data.shared_records); } catch {}
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchReceived(), fetchSent()]).finally(() => setLoading(false));
  }, [fetchReceived, fetchSent]);

  const revokeShare = async (shareId) => {
    try { await api.delete(`/share/${shareId}`); toast.success('Share removed'); fetchReceived(); fetchSent(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const updatePermission = async (shareId, perm) => {
    try { await api.put(`/share/${shareId}`, { permission: perm }); toast.success('Permission updated'); fetchSent(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const ShareRow = ({ r, isSent }) => {
    const { primary, secondary } = recordTitle(r);
    const isSub = ['asset_sub', 'vehicle_sub', 'task_sub'].includes(r.record_type);
    const Icon = isSub ? (SUB_COLL_ICONS[r.sub_collection] || Layers) : (TYPE_ICONS[r.record_type] || Share2);
    return (
      <div className="flex items-center justify-between p-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl hover:shadow-md transition-all"
        data-testid={`shared-${isSent ? 'sent' : 'received'}-${r.id}`}>
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center shrink-0">
            <Icon className="h-5 w-5 text-zinc-500" strokeWidth={1.5} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium truncate">{primary}</span>
              <Badge variant="secondary" className="text-[10px]">{TYPE_LABELS[r.record_type] || r.record_type}</Badge>
              <Badge variant="outline" className="text-[10px]">{r.permission}</Badge>
            </div>
            {secondary && <p className="text-[11px] text-zinc-500 mt-0.5 truncate">{secondary}</p>}
            <p className="text-[11px] text-zinc-400 mt-0.5">{isSent ? `Shared with @${r.shared_with_username}` : `Shared by @${r.shared_by_username}`}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isSent && (
            <Select value={r.permission} onValueChange={v => updatePermission(r.id, v)}>
              <SelectTrigger className="w-24 h-8 text-xs" data-testid={`perm-select-${r.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="view">View</SelectItem>
                <SelectItem value="edit">Edit</SelectItem>
                <SelectItem value="comment">Comment</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Button variant="ghost" size="icon" onClick={() => revokeShare(r.id)} data-testid={`revoke-${isSent ? 'sent' : 'received'}-${r.id}`}>
            <Trash2 className="h-4 w-4 text-red-400" strokeWidth={1.5} />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] overflow-hidden -mb-20 md:-mb-6" data-testid="shared-page">
      <div className="shrink-0 p-4 md:px-8 md:pt-6 md:pb-4 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl sm:text-3xl font-medium tracking-tight">Shared</h2>
            <p className="text-sm text-zinc-500 mt-1">Manage records you've shared or received</p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 md:px-8">
        <div className="w-full">
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="mb-6">
              <TabsTrigger value="received" data-testid="tab-received">Shared With Me ({receivedRecords.length})</TabsTrigger>
              <TabsTrigger value="sent" data-testid="tab-sent">Shared By Me ({sentRecords.length})</TabsTrigger>
            </TabsList>

            {loading ? (
              <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
            ) : (
              <>
                <TabsContent value="received">
                  {receivedRecords.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20">
                      <Share2 className="h-12 w-12 text-zinc-300 mb-4" strokeWidth={1} />
                      <p className="text-zinc-500 text-sm">Nothing shared with you yet</p>
                    </div>
                  ) : (
                    <div className="space-y-2">{receivedRecords.map(r => <ShareRow key={r.id} r={r} isSent={false} />)}</div>
                  )}
                </TabsContent>

                <TabsContent value="sent">
                  {sentRecords.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20">
                      <Share2 className="h-12 w-12 text-zinc-300 mb-4" strokeWidth={1} />
                      <p className="text-zinc-500 text-sm">You haven't shared anything yet</p>
                    </div>
                  ) : (
                    <div className="space-y-2">{sentRecords.map(r => <ShareRow key={r.id} r={r} isSent={true} />)}</div>
                  )}
                </TabsContent>
              </>
            )}
          </Tabs>
        </div>
      </div>
    </div>
  );
}
